import { useContext } from 'react';
import {
    Card,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material';
import { hoursOrMinutes, useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import { ShiftStatusWithdrawAllowed } from '@instapaid/shared-logic';
import { UserContext } from '@/contexts/user/UserContext';
import {
    TableCell,
    TableCellFirstCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell
} from '@/components/shared/styles/Table';
import Chip, { chipColors } from '@/components/shared/Chip';
import { AvailableShift } from '@/services/PaymentsServiceV2';
import CardHeader from '@/components/shared/CardHeader';
import { SMBold } from '@/typography/Typography';
import { Status } from '@/pages/users/UserTransactions';

export type AvailableShiftsTableProps = {
    userId: string;
    payrollPeriod: string;
    shifts?: AvailableShift[];
    shiftStatusWithdrawAllowed?: ShiftStatusWithdrawAllowed;
    payoutType: string;
    companyName: string;
    heading?: string;
    totalHours?: number;
    totalEarnings?: number;
};

export const AvailableShiftsTable = ({
    shifts,
    payoutType,
    heading,
    companyName,
    totalHours,
    totalEarnings
}: AvailableShiftsTableProps) => {
    const { company: adminCompany } = useContext(UserContext);
    const { getBalanceLabelV2, toDecimal } = useFormatting();

    const sortedShifts = shifts?.slice().sort((a, b) => {
        const aTime = a.startTime?.toDate().getTime() || 0;
        const bTime = b.startTime?.toDate().getTime() || 0;
        return aTime - bTime;
    });

    const totalWorkBreak = sortedShifts?.reduce(
        (acc, shift) => acc + shift.break * 60,
        0
    );

    const { t } = useTranslation();
    const { formatDate, formatDateTimeToTime } = useFormatting();

    return (
        <>
            {sortedShifts && sortedShifts?.length > 0 ? (
                <Card>
                    <CardHeader
                        label={heading}
                        description={`${companyName} - ${t('payout_type', {
                            payoutType
                        })}`}
                        chip={
                            <Chip
                                icon={<span></span>}
                                label={
                                    <SMBold
                                        color={chipColors.success.text}
                                    >{`${sortedShifts.length}`}</SMBold>
                                }
                                color={'success'}
                            />
                        }
                    />
                    <Grid md={12}></Grid>

                    <TableContainer component={Paper}>
                        <Table aria-label="shifts table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCellFirstCell>
                                        Date
                                    </TableHeaderCellFirstCell>
                                    <TableHeaderCell>Time</TableHeaderCell>
                                    <TableHeaderCell>Pause</TableHeaderCell>
                                    <TableHeaderCell>
                                        Employee Group
                                    </TableHeaderCell>
                                    <TableHeaderCell>Hours</TableHeaderCell>
                                    <TableHeaderCell
                                        style={{ textAlign: 'right' }}
                                    >
                                        Hourly Rate
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        Hourly Rate From
                                    </TableHeaderCell>
                                    <TableHeaderCell>Status</TableHeaderCell>

                                    <TableHeaderCell align="right">
                                        Earnings
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedShifts?.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCellFirstCell>
                                            {row.startTime
                                                ? formatDate(row.startTime)
                                                : '-'}
                                        </TableCellFirstCell>
                                        <TableCell>
                                            {row.startTime && (
                                                <>
                                                    {formatDateTimeToTime(
                                                        row.startTime
                                                    )}{' '}
                                                    -{' '}
                                                    {row.endTime
                                                        ? formatDateTimeToTime(
                                                              row.endTime
                                                          )
                                                        : '?'}
                                                </>
                                            )}
                                            {!row.startTime && !row.endTime && (
                                                <>Period not reported</>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {hoursOrMinutes(row.break)}{' '}
                                        </TableCell>
                                        <TableCell>{row.groupName}</TableCell>
                                        <TableCell style={{ width: '40px' }}>
                                            {toDecimal(row.duration, 1)}
                                        </TableCell>
                                        <TableCell
                                            style={{ textAlign: 'right' }}
                                        >
                                            {getBalanceLabelV2(
                                                row.hourlyRate,
                                                adminCompany.currency,
                                                { showNegativeStyle: true }
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row.hourlyRateFrom}
                                        </TableCell>

                                        <TableCell style={{ width: '138px' }}>
                                            <Status status={'Available'} />
                                        </TableCell>

                                        <TableCell
                                            align="right"
                                            style={{
                                                color: row.isShiftTypeExcluded
                                                    ? 'grey'
                                                    : undefined
                                            }}
                                            // color={!row.availableForPayout}
                                        >
                                            {getBalanceLabelV2(
                                                row.amountV2,
                                                adminCompany.currency,
                                                { showNegativeStyle: true }
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        You earned
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell>
                                        {toDecimal(totalWorkBreak, 0)} mins
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell style={{ width: '80px' }}>
                                        {toDecimal(totalHours, 2)}
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        {getBalanceLabelV2(
                                            totalEarnings,
                                            adminCompany.currency,
                                            { showNegativeStyle: true }
                                        )}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                        </Table>
                    </TableContainer>
                </Card>
            ) : (
                <Typography textAlign={'center'}>
                    {t('no_available_shifts')}
                </Typography>
            )}
        </>
    );
};
